import { Ban, Loader2, PauseCircle, PlayCircle, RotateCcw, Scale, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatCurrencyWithCommas, formatWeight } from "../../../utils/numberUtils";
import {
  formatRebalancingSplitLabel,
  getRebalancingExplainerSnapshot,
  REBALANCING_EXPLAINER_NLV,
  REBALANCING_PHASE_LABELS,
  REBALANCING_PHASE_ORDER,
  REBALANCING_TARGET_PCT,
  type RebalancingExplainerMode,
  type RebalancingExplainerPhase,
} from "./automatedRebalancingExplainerDemo";

interface AutomatedRebalancingExplainerModalProps {
  isOpen: boolean;
  onClose: () => void;
  appearance?: "default" | "alpha";
}

const AutomatedRebalancingExplainerModal: React.FC<AutomatedRebalancingExplainerModalProps> = ({
  isOpen,
  onClose,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const [mode, setMode] = useState<RebalancingExplainerMode>("active");
  const [phase, setPhase] = useState<RebalancingExplainerPhase>("start");

  const snapshot = useMemo(() => getRebalancingExplainerSnapshot(mode, phase), [mode, phase]);
  const activeStepIndex = REBALANCING_PHASE_ORDER.indexOf(phase);
  const isApplyPhase = phase === "apply";
  const isPausedApply = isApplyPhase && mode === "paused";
  const isActiveApply = isApplyPhase && mode === "active";
  const isBalanced = snapshot.maxDriftPct === 0;

  const resetToStart = useCallback(() => {
    setPhase("start");
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetToStart();
  }, [isOpen, resetToStart]);

  useEffect(() => {
    resetToStart();
  }, [mode, resetToStart]);

  if (!isOpen) return null;

  const microLabelClass = isAlpha
    ? "text-[10px] font-semibold uppercase tracking-wider text-v7-ink/55"
    : "text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400";
  const cardClass = isAlpha
    ? "rounded-v7-panel border border-v7-ink/15 bg-v7-white"
    : "rounded-lg border border-gray-200 bg-white dark:border-rp-separator dark:bg-black";
  const strongTextClass = isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white";
  const bodyTextClass = isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300";

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${isAlpha ? "bg-v7-ink/60" : "bg-black/70"}`}>
      <div
        data-appearance={appearance}
        className={`flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden border shadow-2xl ${
          isAlpha
            ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans"
            : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"
        }`}
      >
        <div className={`flex items-start justify-between border-b px-5 py-4 ${isAlpha ? "border-v7-ink/10" : "border-gray-200 dark:border-rp-separator"}`}>
          <div>
            <h2 className={`text-base font-bold leading-tight ${strongTextClass}`}>
              How automated rebalancing works
            </h2>
            <p className={`mt-1 text-sm ${bodyTextClass}`}>
              Simple example: a $100,000 two-fund strategy targets 50 / 50, then drifts to 60 / 40.
            </p>
            <p className={`mt-2 text-xs ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
              Click each step below to walk through the example at your own pace.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={isAlpha
              ? "rounded-v7-control p-1 text-v7-ink/45 transition-colors hover:bg-v7-paper hover:text-v7-ink"
              : "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-rp-surface dark:hover:text-white"}
            aria-label="Close explainer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className={`inline-flex border p-0.5 ${isAlpha ? "rounded-v7-control border-v7-ink/20 bg-v7-white" : "rounded-lg border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface"}`}>
              {(["active", "paused"] as RebalancingExplainerMode[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setMode(option)}
                  className={
                    mode === option
                      ? isAlpha
                        ? "inline-flex items-center gap-1.5 rounded-v7-control bg-v7-bronze/10 px-3 py-1.5 text-xs font-semibold text-v7-bronze-dark transition-colors"
                        : "inline-flex items-center gap-1.5 rounded-md bg-rp-amber px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                      : isAlpha
                        ? "inline-flex items-center gap-1.5 rounded-v7-control px-3 py-1.5 text-xs font-semibold text-v7-ink-soft transition-colors hover:text-v7-ink"
                        : "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }
                >
                  {option === "active" ? <PlayCircle className="h-3.5 w-3.5" /> : <PauseCircle className="h-3.5 w-3.5" />}
                  {option === "active" ? "Trading active" : "Rebalancing paused"}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={resetToStart}
              className={isAlpha
                ? "inline-flex items-center gap-2 rounded-v7-control border border-v7-ink/25 bg-v7-white px-3 py-1.5 text-xs font-semibold text-v7-ink-soft transition-colors hover:border-v7-ink"
                : "inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Back to step 1
            </button>
          </div>

          <div className={`relative overflow-hidden border p-5 transition-colors duration-300 ${
            isPausedApply
              ? isAlpha
                ? "rounded-v7-panel border-v7-rust/40 bg-v7-rust/5"
                : "rounded-2xl border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30"
              : isAlpha
                ? "rounded-v7-panel border-v7-ink/15 bg-v7-paper"
                : "rounded-2xl border-gray-200 bg-gray-50 dark:border-rp-separator dark:bg-rp-surface-sunken"
          }`}>
            <div className={`pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full blur-2xl ${isAlpha ? "bg-v7-bronze/10" : "bg-rp-amber/10"}`} />
            <div className={`pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full blur-2xl ${
              isPausedApply
                ? isAlpha ? "bg-v7-rust/10" : "bg-red-500/10"
                : isAlpha ? "bg-v7-success/10" : "bg-green-500/10"
            }`} />

            <div className="relative space-y-5">
              {isApplyPhase && snapshot.applyHeadline && (
                <div className={`rounded-lg border px-4 py-3 ${
                  isPausedApply
                    ? isAlpha
                      ? "border-v7-rust/40 bg-v7-rust/10"
                      : "border-red-300 bg-red-100 dark:border-red-800 dark:bg-red-900/30"
                    : isAlpha
                      ? "border-v7-success/30 bg-v7-success/10"
                      : "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
                }`}>
                  <div className="flex items-start gap-3">
                    {isPausedApply ? (
                      <PauseCircle className={`rebalancing-explainer-pause h-6 w-6 shrink-0 ${isAlpha ? "text-v7-rust" : "text-red-600 dark:text-red-400"}`} />
                    ) : (
                      <Loader2 className={`h-6 w-6 shrink-0 animate-spin ${isAlpha ? "text-v7-success" : "text-green-600 dark:text-green-500"}`} />
                    )}
                    <div>
                      <p className={`text-sm font-bold uppercase tracking-wider ${
                        isPausedApply
                          ? isAlpha ? "text-v7-rust" : "text-red-700 dark:text-red-300"
                          : isAlpha ? "text-v7-success" : "text-green-700 dark:text-green-400"
                      }`}>
                        {snapshot.applyHeadline}
                      </p>
                      {isPausedApply && (
                        <p className={`mt-1 text-sm ${isAlpha ? "text-v7-rust" : "text-red-800 dark:text-red-200"}`}>
                          Your user setting blocks every automated rebalance order. No buys. No sells. The full strategy stays on pause.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className={`px-4 py-3 ${cardClass}`}>
                <p className={microLabelClass}>
                  Your target split
                </p>
                <p className={`mt-1 text-sm font-semibold ${strongTextClass}`}>
                  Fund A and Fund B at {REBALANCING_TARGET_PCT}% / {REBALANCING_TARGET_PCT}%
                </p>
                <p className={`mt-1 text-xs ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
                  System trading must be ready before any automated orders can run.
                </p>
              </div>

              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className={microLabelClass}>
                    Total account value
                  </p>
                  <p className={`mt-1 font-mono text-3xl font-bold tabular-nums transition-all duration-700 ${strongTextClass}`}>
                    {formatCurrencyWithCommas(REBALANCING_EXPLAINER_NLV, 0)}
                  </p>
                </div>

                <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${
                  isAlpha
                    ? "border-v7-ink/15 bg-v7-white text-v7-ink-soft"
                    : "border-gray-200 bg-white text-gray-700 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-200"
                }`}>
                  <Scale className={`h-4 w-4 ${isAlpha ? "text-v7-bronze-dark" : "text-rp-amber"}`} />
                  Drift {formatWeight(snapshot.maxDriftPct, 0)}%
                </div>
              </div>

              <div>
                <div className={`mb-2 flex items-center justify-between ${microLabelClass}`}>
                  <span>Current allocation</span>
                  <span>{REBALANCING_PHASE_LABELS[phase]}</span>
                </div>

                <div className={`relative h-14 overflow-hidden border ${
                  isAlpha ? "rounded-v7-panel bg-v7-white" : "rounded-xl bg-white dark:bg-black"
                } ${
                  isPausedApply
                    ? isAlpha ? "border-v7-rust/40 opacity-80" : "border-red-300 opacity-80 dark:border-red-800"
                    : isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"
                }`}>
                  <div className="absolute inset-0 flex">
                    {snapshot.positions.map((position, index) => (
                      <div
                        key={position.symbol}
                        className={`relative h-full transition-all duration-700 ease-out ${
                          index === 0
                            ? isAlpha ? "bg-v7-bronze" : "bg-rp-amber"
                            : isAlpha ? "bg-v7-success" : "bg-teal-500 dark:bg-teal-600"
                        } ${isPausedApply ? "saturate-50" : ""}`}
                        style={{ width: `${position.currentPct}%` }}
                      >
                        <span className={`absolute inset-y-0 flex items-center text-xs font-semibold ${
                          index === 0
                            ? isAlpha ? "left-3 text-v7-white" : "left-3 text-black"
                            : isAlpha ? "right-3 text-v7-white" : "right-3 text-white"
                        }`}>
                          {position.symbol}
                        </span>
                      </div>
                    ))}
                  </div>
                  {isActiveApply && (
                    <div className={`rebalancing-explainer-shimmer pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent to-transparent ${isAlpha ? "via-white/30" : "via-white/30 dark:via-white/10"}`} />
                  )}
                  {isPausedApply && (
                    <div className={`pointer-events-none absolute inset-0 flex items-center justify-center ${isAlpha ? "bg-v7-rust/10" : "bg-red-950/10 dark:bg-red-950/25"}`}>
                      <PauseCircle className={`h-8 w-8 ${isAlpha ? "text-v7-rust/80" : "text-red-600/80 dark:text-red-400"}`} aria-hidden="true" />
                    </div>
                  )}
                </div>
              </div>

              {isApplyPhase && snapshot.tradeOrders && (
                <div className={`overflow-hidden rounded-xl border ${
                  isPausedApply
                    ? isAlpha ? "border-v7-rust/40" : "border-red-300 dark:border-red-800"
                    : isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"
                }`}>
                  <div className={`border-b px-4 py-2 text-[10px] font-semibold uppercase tracking-wider ${
                    isPausedApply
                      ? isAlpha
                        ? "border-v7-rust/30 bg-v7-rust/10 text-v7-rust"
                        : "border-red-200 bg-red-100 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                      : isAlpha
                        ? "border-v7-ink/10 bg-v7-paper text-v7-ink/55"
                        : "border-gray-200 bg-gray-50 text-gray-500 dark:border-rp-separator dark:bg-rp-surface-sunken dark:text-gray-400"
                  }`}>
                    {isPausedApply ? "Every planned trade blocked" : "Rebalance orders in progress"}
                  </div>
                  <div className={`divide-y ${isAlpha ? "divide-v7-ink/10 bg-v7-white" : "divide-gray-200 bg-white dark:divide-rp-separator dark:bg-black"}`}>
                    {snapshot.tradeOrders.map((order) => (
                      <div
                        key={order.symbol}
                        className={`flex items-center justify-between gap-3 px-4 py-3 ${
                          order.status === "blocked"
                            ? isAlpha ? "bg-v7-rust/5" : "bg-red-50/80 dark:bg-red-900/10"
                            : ""
                        }`}
                      >
                        <div>
                          <p className={`text-sm font-semibold ${strongTextClass}`}>{order.symbol}</p>
                          <p className={`text-xs font-medium ${
                            order.status === "blocked"
                              ? isAlpha ? "text-v7-rust line-through" : "text-red-700 line-through dark:text-red-300"
                              : bodyTextClass
                          }`}>
                            {order.action}
                          </p>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider ${
                          order.status === "blocked"
                            ? isAlpha ? "text-v7-rust" : "text-red-600 dark:text-red-400"
                            : isAlpha ? "text-v7-success" : "text-green-600 dark:text-green-500"
                        }`}>
                          {order.status === "blocked" ? (
                            <>
                              <Ban className="h-3.5 w-3.5" />
                              Blocked
                            </>
                          ) : (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Sending
                            </>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {snapshot.positions.map((position) => (
                  <div
                    key={position.symbol}
                    className={`p-4 ${cardClass}`}
                  >
                    <p className={`text-sm font-semibold ${strongTextClass}`}>{position.symbol}</p>
                    <p className={`mt-1 font-mono text-xl font-bold tabular-nums transition-all duration-700 ${strongTextClass}`}>
                      {formatCurrencyWithCommas(position.dollars, 0)}
                    </p>
                    <p className={`mt-1 font-mono text-sm tabular-nums ${bodyTextClass}`}>
                      {formatWeight(position.currentPct, 0)}% now · {formatWeight(position.targetPct, 0)}% target
                    </p>
                  </div>
                ))}
              </div>

              {snapshot.plannedAction && !isApplyPhase && (
                <div className={`px-4 py-3 text-sm ${isAlpha ? "rounded-v7-panel border border-v7-ink/15 bg-v7-white text-v7-ink-soft" : "rounded-lg border border-gray-200 bg-white text-gray-600 dark:border-rp-separator dark:bg-black dark:text-gray-300"}`}>
                  {snapshot.plannedAction}
                </div>
              )}

              {snapshot.plannedAction && isApplyPhase && (
                <div className={`rounded-lg border px-4 py-3 text-sm ${
                  isPausedApply
                    ? isAlpha
                      ? "border-v7-rust/40 bg-v7-white text-v7-rust"
                      : "border-red-300 bg-white text-red-800 dark:border-red-800 dark:bg-black dark:text-red-200"
                    : isAlpha
                      ? "border-v7-ink/15 bg-v7-white text-v7-ink-soft"
                      : "border-gray-200 bg-white text-gray-600 dark:border-rp-separator dark:bg-black dark:text-gray-300"
                }`}>
                  {snapshot.plannedAction}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {REBALANCING_PHASE_ORDER.map((step, index) => (
                  <button
                    key={step}
                    type="button"
                    onClick={() => setPhase(step)}
                    aria-current={index === activeStepIndex ? "step" : undefined}
                    className={
                      index === activeStepIndex
                        ? isAlpha
                          ? "rounded-v7-control border border-v7-bronze-dark bg-v7-bronze/10 px-2 py-2 text-center transition-colors hover:bg-v7-bronze/15"
                          : "rounded-lg border border-rp-amber bg-rp-amber/10 px-2 py-2 text-center transition-colors hover:bg-rp-amber/15"
                        : isAlpha
                          ? "rounded-v7-control border border-v7-ink/15 bg-v7-white px-2 py-2 text-center transition-colors hover:border-v7-bronze/40 hover:bg-v7-paper"
                          : "rounded-lg border border-gray-200 bg-white px-2 py-2 text-center transition-colors hover:border-rp-amber/40 hover:bg-gray-50 dark:border-rp-separator dark:bg-black dark:hover:bg-rp-surface"
                    }
                  >
                    <p className={microLabelClass}>
                      Step {index + 1}
                    </p>
                    <p className={`mt-1 text-xs font-semibold ${strongTextClass}`}>
                      {REBALANCING_PHASE_LABELS[step]}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={`p-4 ${cardClass}`}>
            <p className={`text-sm ${bodyTextClass}`}>
              {mode === "active" ? (
                <>
                  With <span className={`font-semibold ${strongTextClass}`}>trading active</span>, automated rebalancing can place orders to move the account back toward target weights.
                </>
              ) : (
                <>
                  With <span className={`font-semibold ${strongTextClass}`}>rebalancing paused</span>, drift can remain because no new automated orders are placed. Existing positions stay as they are.
                </>
              )}
            </p>
            {phase === "complete" && (
              <p className={`mt-2 text-xs font-semibold uppercase tracking-wider ${
                isBalanced
                  ? isAlpha ? "text-v7-success" : "text-green-600 dark:text-green-500"
                  : isAlpha ? "text-v7-bronze-dark" : "text-yellow-600 dark:text-yellow-500"
              }`}>
                {isBalanced
                  ? `Target restored: ${formatRebalancingSplitLabel(snapshot.positions)}`
                  : `Drift remains: ${formatRebalancingSplitLabel(snapshot.positions)}`}
              </p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes rebalancingExplainerShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .rebalancing-explainer-shimmer {
          animation: rebalancingExplainerShimmer 1.1s ease-in-out infinite;
        }
        @keyframes rebalancingExplainerPausePulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.85; }
        }

        .rebalancing-explainer-pause {
          animation: rebalancingExplainerPausePulse 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AutomatedRebalancingExplainerModal;
